import { Button, Divider, Popover, Radio, RadioChangeEvent, Space, DatePicker } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { FilterIcon } from "../icons";
import { Search } from "./Search";
import { IFilterOption } from "@interfaces";
import { enumFilter } from "@configs";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useContext, useState } from "react";
import { ModalContext } from "@utils";
import { useSelector } from "react-redux";
import { selectApp } from "@redux";
import { SharedIconButton } from "../shared";
import { SearchLoader } from "./SearchLoader";

const { RangePicker } = DatePicker;

const overrideWidth = { minWidth: 99, borderRadius: 6 };
const overridePopover = {
  minWidth: "224px",
  background: "rgb(255, 255, 255)",
  border: "1px solid rgba(240, 109, 36, 0.12)",
  boxShadow: "rgba(0, 0, 0, 0.2) 0px 5px 8px",
  borderRadius: "6px",
};
const fullWidth = { width: "100%" };
const dateStyle = { width: "100%", height: 48 };
interface IProps {
  actionBtn?: React.ReactNode;
  filter?: IFilterOption[];
  callbackFun: (value: string) => void;
  onDateChange?: (dates: null | (Dayjs | null)[], dateStrings: string[]) => void;
  searchText: (value: string) => void;
  searchInputPlaceholder: string | any;
  selectedOption: string | any;
  advanceBtn?: React.ReactNode;
}

const rangePresets: {
  label: string;
  value: [Dayjs, Dayjs];
}[] = [
  { label: "Last 7 Days", value: [dayjs().add(-7, "d"), dayjs()] },
  { label: "Last 14 Days", value: [dayjs().add(-14, "d"), dayjs()] },
  { label: "Last 30 Days", value: [dayjs().add(-30, "d"), dayjs()] },
  { label: "Last 90 Days", value: [dayjs().add(-90, "d"), dayjs()] },
];

export const SearchFilter = ({
  actionBtn,
  filter,
  callbackFun,
  onDateChange,
  searchText,
  searchInputPlaceholder,
  selectedOption,
  advanceBtn,
}: IProps) => {
  const { t } = useTranslation();
  const modal = useContext(ModalContext);
  const { mobile } = useSelector(selectApp);
  const [refresh, setRefresh] = useState<boolean>(false);
  const handleFilter = (value: string) => {
    if (value === enumFilter.DATE) {
      dateFilter();
    } else {
      setRefresh(true);
      setTimeout(() => {
        callbackFun(value);
        setRefresh(false);
      }, 200);
    }
  };
  const dateFilter = () => {
    modal.info({
      title: "Please select the date range",
      width: 640,
      content: (
        <div>
          <RangePicker
            style={dateStyle}
            presets={rangePresets}
            onChange={onDateChange}
            size="large"
          />
        </div>
      ),
      cancelText: t("page.cancel"),
      okText: t("page.select"),
      onOk() {
        () => console.log("ok");
      },
    });
  };
  return (
    <StyledFilter>
      <Popover
        placement="bottomLeft"
        title={null}
        content={<FilterOptions filter={filter} selected={selectedOption} advanceBtn={advanceBtn} onChange={handleFilter} />}
        arrow={false}
        trigger="click"
        overlayStyle={overridePopover}
      >
        <StyledBtn>
          {mobile ? (
            <SharedIconButton icon={<FilterIcon />} />
          ) : (
            <Button style={overrideWidth} icon={<FilterIcon />}>
              {t("page.filter")}
            </Button>
          )}
        </StyledBtn>
      </Popover>
      {refresh ? <SearchLoader /> : <Search inputPlaceholder={searchInputPlaceholder} searchInput={searchText} />}
      {actionBtn || null}
    </StyledFilter>
  );
};

const StyledBtn = styled.div`
  .ant-btn {
    display: flex;
    align-items: center;
    column-gap: 10px;
  }
`;
interface IPropsFilter {
  filter?: IFilterOption[];
  selected: string;
  onChange: (value: string) => void;
  advanceBtn?: React.ReactNode;
}
const FilterOptions = ({ filter, selected, onChange, advanceBtn }: IPropsFilter) => {
  const { t } = useTranslation();
  const handleChooseOption = (e: RadioChangeEvent) => {
    onChange(e.target.value);
  };
  return (
    <FilterSpace direction="vertical">
      <Radio.Group style={fullWidth} onChange={handleChooseOption} value={selected}>
        {filter &&
          filter.map((item, index) => (
            <div key={index}>
              <FilterSpace direction="vertical">
                <p className="title">{t(`page.${item.label}`)}</p>
                {item.children.map((child, key) => (
                  <StyledRadio value={child} key={key}>
                    <span className="label-name">{t(`page.${child}`)}</span>
                  </StyledRadio>
                ))}
              </FilterSpace>
              {index !== filter.length - 1 && <Divider />}
            </div>
          ))}
      </Radio.Group>
      {advanceBtn && (
        <>
          <Divider />
          <Space>{advanceBtn || null}</Space>
        </>
      )}
    </FilterSpace>
  );
};
const StyledRadio = styled(Radio)`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.05em;
  color: #25213b;
  span {
    flex: 1;
    &.ant-radio {
      flex: initial;
    }
  }
  .label-name {
    flex: 1;
  }
`;
const FilterSpace = styled(Space)`
  width: 100%;
  .ant-space-item {
    width: 100%;
  }
  .title {
    font-weight: 400;
    font-size: 12px;
    line-height: 15px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #6e6893;
    padding: 0 8px;
  }
`;
const StyledFilter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 23px 32px;
  column-gap: 32px;
`;
